import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Divider } from "../../../components";
import { Spacer } from "../../spacer";
import styles from "./template_partner_portal_index_v2.module.css";
import { useMediaQuery } from "../../../hooks";
import { Partner } from "../../api";
import { PartnerConfig } from "../../partner_config";
import {
  lazyWithRetry,
  useLazyWithoutRetry,
} from "../../../core/lazy_with_retry";
import { PortalButton } from "../components/portal_button";
import { usePortalHistory } from "../hooks/use_portal_history";
import { useAuthV2 } from "@/offering/auth_portal_custom_login_provider";
import { usePortalAuthFeatureFlag } from "@/core/splitio";
import { useAuth0 } from "@auth0/auth0-react";

const DefaultTheme = lazyWithRetry(
  () => import("../../../offering_v2/components/default_theme")
);

interface TemplatePartnerPortalIndexProps {
  partnerConfig: PartnerConfig;
  partner: Partner;
  children: JSX.Element;
}

export function TemplatePartnerPortalIndexV2({
  partnerConfig,
  partner,
  children,
}: TemplatePartnerPortalIndexProps) {
  const { slug, favicon } = partnerConfig;
  const { device } = useMediaQuery();
  const portalAuthFeatureFlag = usePortalAuthFeatureFlag();
  const { logout: customLogout } = useAuthV2();
  const { logout: auth0Logout } = useAuth0();
  const logout = portalAuthFeatureFlag ? customLogout : auth0Logout;

  const PartnerTheme = useLazyWithoutRetry(
    () => import(`../../../offering/${slug}/${slug}_theme.tsx`)
  );

  return (
    <div>
      <DefaultTheme />
      <PartnerTheme />
      <PortalHead title={partner.name} favicon={favicon} />
      <PortalHeader logo={partner.logoUrl} slug={slug} />
      <div className={styles.contentWrapper}>
        {children}

        <div className={styles.footerWrapper}>
          {device === "mobile" && (
            <div>
              <PortalButton appearance="text" onClick={() => logout()}>
                Logout
              </PortalButton>
              <Spacer size={30} />
            </div>
          )}
          <div>
            Powered by Flexspace&reg; |{" "}
            <a
              href="https://meet-production.s3-us-west-1.amazonaws.com/documents/flexspace-privacy.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              Privacy
            </a>{" "}
            |{" "}
            <a
              href="https://meet-production.s3-us-west-1.amazonaws.com/documents/flexspace-terms.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              Terms
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

interface PortalHeadProps {
  title: string;
  favicon: string;
}

function PortalHead(props: PortalHeadProps) {
  const { title, favicon } = props;

  return (
    <Helmet>
      <title>{title} Portal</title>
      <link href={favicon} rel="shortcut icon" type="image/x-icon" />
    </Helmet>
  );
}

interface PortalHeaderProps {
  logo: string;
  slug: string;
}

function PortalHeader(props: PortalHeaderProps) {
  const { logo } = props;
  const { device } = useMediaQuery();
  const { toHome } = usePortalHistory();
  const { logout: customLogout } = useAuthV2();
  const { logout: auth0Logout } = useAuth0();
  const logout = usePortalAuthFeatureFlag() ? customLogout : auth0Logout;
  const [imageClassName, setImageClassName] = useState<string>();

  const handleLoadImage = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = event.currentTarget;
    const ratio = width / height;
    const defaultRatio = 3;
    const mediumRatio = 2;
    if (ratio > defaultRatio) {
      setImageClassName(styles.largeLogo);
    } else if (ratio > mediumRatio) {
      setImageClassName(styles.mediumLogo);
    } else {
      setImageClassName(styles.smallLogo);
    }
  };

  return (
    <div className={styles.headerWrapper}>
      <div className={styles.header}>
        <div onClick={toHome}>
          <img
            className={imageClassName}
            alt="logo"
            src={logo}
            onLoad={handleLoadImage}
          />
        </div>
        <div className={styles.headerActions}>
          {device !== "mobile" && (
            <PortalButton appearance="text" onClick={logout}>
              Logout
            </PortalButton>
          )}
        </div>
      </div>
      <Divider />
    </div>
  );
}
